// Shared helpers for the indexable "idiotic statement" pages
// (/idiotic-statements and /idiotic-statements/:slug).
//
// Dependency-free CommonJS on purpose: the React pages, the SSG script
// (scripts/generate-static-pages.js), the sitemap and llms.txt generators all
// load this file, so every environment derives identical slugs, titles and
// related-links. The old /s/{slug}.html share pages (noindex, social previews
// only) are NOT touched by any of this; they keep their own underscore slugs.

const { slugify } = require('./slugify');
const { fitMeta } = require('./seoMeta');

// URL slug for an indexable page: same normalization as the share-page slug
// (quotes/commas/$ stripped, lowercased) but hyphens, which read better in SERPs.
const statementSlug = (title) => slugify(title).replace(/_/g, '-');

const truthParagraphs = (s) => (Array.isArray(s.truth) ? s.truth : [s.truth]).filter(Boolean);

const verdictOf = (s) => String(s.titleBadge || '').trim(); // "False" | "True" | "Misleading"

// <title> text before the " | EVs for Idiots" suffix (added only when it fits in
// 60 characters). A statement too long for "{title}: {verdict}" carries a
// shorter hand-written `seoTitle` in sampleContent.js.
const statementTitle = (s) => {
  const full = `${s.title}: ${verdictOf(s)}`;
  if (full.length <= 60 || !s.seoTitle) return full;
  return s.seoTitle;
};

// Split after sentence-ending punctuation. Deliberately no regex lookbehind: Safari
// before 16.4 cannot parse one, and a parse error in this chunk blanks the page.
function splitSentences(text) {
  return String(text || '').replace(/([.!?])\s+/g, '$1\u0001').split('\u0001');
}

// Meta description: the verdict plus the opening sentence(s) of the real answer,
// cut at a sentence boundary so it never ends mid-thought; when whole sentences
// cannot reach 120 characters, it is cut at a word boundary instead.
function statementDescription(s) {
  const lead = `Verdict: ${verdictOf(s)}. `;
  const text = truthParagraphs(s)[0] || '';
  return fitMeta(lead + text, [], 158);
}

// The opening sentence, used as the short answer above the full answer.
function statementLede(s) {
  const text = truthParagraphs(s)[0] || '';
  const sentences = splitSentences(text);
  return sentences.slice(0, 1).join(' ');
}

// One tool to point at, chosen by what the statement is about. Tool links only,
// no prices anywhere on these pages.
function statementCta(s) {
  const cats = s.categories || [];
  const has = (...names) => names.some((n) => cats.includes(n));
  if (has('Cost')) {
    return {
      to: '/savings-calculator',
      title: 'Run your own numbers',
      blurb: "Averages don't drive your car. Plug in your miles, your gas price and your local electricity rate and see what an EV would actually cost you.",
      button: 'Open the savings calculator',
    };
  }
  if (has('Subsidies', 'Politics & Policy')) {
    return {
      to: '/incentives',
      title: 'See what your state actually offers',
      blurb: 'The federal credit is gone, but plenty of state and utility money is still on the table. It changes by state, so check yours.',
      button: 'Check state incentives',
    };
  }
  if (has('Charging')) {
    return {
      to: '/chargers',
      title: 'Compare home chargers',
      blurb: 'Most charging happens at home overnight. Here is how the common home chargers stack up.',
      button: 'Compare chargers',
    };
  }
  if (has('Range', 'Cold weather')) {
    return {
      to: '/find-your-ev',
      title: 'Find an EV with the range you need',
      blurb: 'Range varies a lot by model. Filter real EVs by range, body type and budget style and skip the guessing.',
      button: 'Find your EV',
    };
  }
  return {
    to: '/find-your-ev',
    title: 'Ready to look at real EVs?',
    blurb: 'We match you to the right electric vehicle for how you actually drive, with no dealer pitch.',
    button: 'Find your EV',
  };
}

// Up to n other statements: same category first, then shared idiot type, then
// whatever is nearest by id so the list is always full and always deterministic.
function relatedStatements(all, current, n = 4) {
  const others = all.filter((s) => s.id !== current.id);
  const score = (s) => {
    const sameCat = (s.categories || []).some((c) => (current.categories || []).includes(c)) ? 2 : 0;
    const sameType = (s.idiotTypes || []).some((t) => (current.idiotTypes || []).includes(t)) ? 1 : 0;
    return sameCat + sameType;
  };
  return others
    .map((s) => ({ s, score: score(s), gap: Math.abs(s.id - current.id) }))
    .sort((a, b) => b.score - a.score || a.gap - b.gap)
    .slice(0, n)
    .map((x) => x.s);
}

const badgeColors = {
  False: { bg: '#D7140F', color: '#fff', border: '#D7140F' },
  Misleading: { bg: '#FFE600', color: '#111', border: '#111' },
  True: { bg: '#1748E8', color: '#fff', border: '#1748E8' },
};

module.exports = {
  statementSlug,
  truthParagraphs,
  verdictOf,
  statementTitle,
  statementDescription,
  statementLede,
  statementCta,
  relatedStatements,
  badgeColors,
};
